import React from 'react';
import styled from 'styled-components';
import Popover from '@mui/material/Popover';

import type { DocumentDate } from '../types';
import {
    buildDocumentDate,
    daysInMonth,
    documentDateComponents,
    noDateDocumentDate,
} from '../utils/documentDate';
import { MONTH_NAMES, formatDocumentDate } from '../utils/format';

/**
 * The single, shared document date popup: tiered Year / Month / Day fields.
 *
 * Dates on legal documents are often only PARTIALLY known, so precision is a
 * property of the value, not a mode: each date component has exactly ONE
 * control, and the Month/Day selects carry an explicit "Unknown" option that
 * states the intent directly ("the month is unknown") rather than a bare
 * placeholder. Correcting one component — the common case — touches only that
 * field; reducing precision is the deliberate act of setting a component to
 * Unknown (clearing the month also clears the day).
 *
 * Commit is explicit: Done saves exactly what the preview line shows, No date
 * saves the no-date sentinel, and Cancel / Escape / click-away discard.
 *
 * Both the inspector header and the table cell open THIS component, so date
 * editing behaves identically everywhere. The host owns open/anchor state and
 * renders its own trigger (pencil/label); onChange reports the finished edit.
 */

const POPUP_WIDTH = 268;

const Body = styled.form`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: ${POPUP_WIDTH}px;
    padding: 14px 16px;
    font-size: 13px;
    color: #0f172a;
`;

const FieldRow = styled.div`
    display: flex;
    gap: 10px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const FieldLabel = styled.label`
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
`;

const YearInput = styled.input`
    box-sizing: border-box;
    width: 62px;
    padding: 5px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    color: inherit;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
    }
    &:disabled {
        opacity: 0.5;
    }
`;

const FieldSelect = styled.select`
    box-sizing: border-box;
    padding: 5px 6px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    background: #fff;
    color: inherit;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Preview = styled.div`
    font-size: 12px;
    color: #6b7280;

    strong {
        color: #0f172a;
        font-weight: 600;
    }
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Spacer = styled.div`
    flex: 1;
`;

const GhostButton = styled.button`
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 4px 10px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #f8fafc;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PrimaryButton = styled.button`
    border: 1px solid #3b82f6;
    border-radius: 6px;
    padding: 4px 12px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    background: #3b82f6;
    color: #fff;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #2f6fd6;
        border-color: #2f6fd6;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const UNKNOWN = '';

const parseYear = (text: string): number | null => (text.length === 4 ? Number(text) : null);

export interface DocumentDatePickerProps {
    /** Current stored date (used to seed the fields on open). */
    date?: DocumentDate | null;
    open: boolean;
    /** Kept for host-interface compatibility; the popup itself never opens itself. */
    onOpen?: () => void;
    onClose: () => void;
    /** Anchor for the popover (the host's trigger button). */
    anchorEl?: HTMLElement | null;
    disabled?: boolean;
    /**
     * Report a finished edit: a DocumentDate at the precision implied by the
     * filled fields, or the no-date sentinel ({ noDate: true }). May be async;
     * the host persists and the popup closes.
     */
    onChange: (next: DocumentDate | null) => void | Promise<void>;
}

export const DocumentDatePicker: React.FC<DocumentDatePickerProps> = ({
    date,
    open,
    onClose,
    anchorEl,
    disabled,
    onChange,
}) => {
    const uid = React.useId();
    const [yearText, setYearText] = React.useState('');
    const [month, setMonth] = React.useState<number | null>(null);
    const [day, setDay] = React.useState<number | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);

    // Reseed from the stored value on every open (and if the underlying date
    // changes while open, e.g. switching documents). Keyed on the individual
    // date fields rather than the `date` object identity.
    React.useEffect(() => {
        if (!open) return;
        const seeded = documentDateComponents(date);
        setYearText(seeded.year != null ? String(seeded.year) : '');
        setMonth(seeded.month);
        setDay(seeded.day);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, date?.normalized, date?.year, date?.month, date?.day, date?.noDate]);

    const yearNum = parseYear(yearText);
    const busy = Boolean(disabled) || isSaving;

    const clampDay = (nextYear: number | null, nextMonth: number | null, currentDay: number | null) => {
        if (nextMonth == null) return null;
        if (currentDay == null) return null;
        return Math.min(currentDay, daysInMonth(nextYear, nextMonth));
    };

    const handleYearText = (text: string) => {
        const digits = text.replace(/\D/g, '').slice(0, 4);
        setYearText(digits);
        setDay(prev => clampDay(parseYear(digits), month, prev));
    };

    const handleMonth = (value: string) => {
        if (value === UNKNOWN) {
            // The month is unknown => the day necessarily is too.
            setMonth(null);
            setDay(null);
            return;
        }
        const nextMonth = Number(value);
        setMonth(nextMonth);
        setDay(prev => clampDay(yearNum, nextMonth, prev));
    };

    const handleDay = (value: string) => {
        setDay(value === UNKNOWN ? null : Number(value));
    };

    const commit = async (next: DocumentDate) => {
        setIsSaving(true);
        try {
            await onChange(next);
        } finally {
            setIsSaving(false);
        }
        onClose();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (yearNum == null || busy) return;
        await commit(buildDocumentDate(yearNum, month, day));
    };

    const handleNoDate = async () => {
        if (busy) return;
        await commit(noDateDocumentDate());
    };

    const dayCount = month != null ? daysInMonth(yearNum, month) : 0;
    const preview = yearNum != null ? formatDocumentDate(buildDocumentDate(yearNum, month, day)) : null;

    return (
        <Popover
            open={open && Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => {
                if (!isSaving) onClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
                paper: {
                    elevation: 0,
                    sx: {
                        mt: 0.5,
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                    },
                },
            }}
        >
            <Body onSubmit={handleSubmit}>
                <FieldRow>
                    <Field>
                        <FieldLabel htmlFor={`${uid}-year`}>Year</FieldLabel>
                        <YearInput
                            id={`${uid}-year`}
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder="YYYY"
                            autoFocus
                            value={yearText}
                            disabled={busy}
                            onChange={e => handleYearText(e.target.value)}
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor={`${uid}-month`}>Month</FieldLabel>
                        <FieldSelect
                            id={`${uid}-month`}
                            value={month != null ? String(month) : UNKNOWN}
                            disabled={busy}
                            onChange={e => handleMonth(e.target.value)}
                        >
                            <option value={UNKNOWN}>Unknown</option>
                            {MONTH_NAMES.map((name, index) => (
                                <option key={name} value={String(index + 1)}>
                                    {name}
                                </option>
                            ))}
                        </FieldSelect>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor={`${uid}-day`}>Day</FieldLabel>
                        <FieldSelect
                            id={`${uid}-day`}
                            value={day != null ? String(day) : UNKNOWN}
                            disabled={busy || month == null}
                            onChange={e => handleDay(e.target.value)}
                        >
                            <option value={UNKNOWN}>Unknown</option>
                            {Array.from({ length: dayCount }, (_, i) => (
                                <option key={i + 1} value={String(i + 1)}>
                                    {i + 1}
                                </option>
                            ))}
                        </FieldSelect>
                    </Field>
                </FieldRow>
                <Preview aria-live="polite">
                    {preview != null ? (
                        <>
                            Saves as <strong>{preview}</strong>
                        </>
                    ) : (
                        'Enter a year, or choose "No date".'
                    )}
                </Preview>
                <Footer>
                    <GhostButton
                        type="button"
                        onClick={handleNoDate}
                        disabled={busy}
                        title="The document has no date"
                    >
                        No date
                    </GhostButton>
                    <Spacer />
                    <GhostButton type="button" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </GhostButton>
                    <PrimaryButton type="submit" disabled={busy || yearNum == null}>
                        Done
                    </PrimaryButton>
                </Footer>
            </Body>
        </Popover>
    );
};

export default DocumentDatePicker;
